import React, { useEffect, useState, useRef } from 'react';
import './Skills.css';
import { getSkills } from '../queries/getSkills';
import { Skill } from '../types';
import { 
  FaReact, FaNodeJs, FaAws, FaDocker, FaGitAlt, FaJava, 
  FaPlay, FaPlus, FaDatabase, FaLinux,
  FaTerminal, FaStar, FaFire, FaRocket, FaLayerGroup, FaTh, FaChevronRight
} from 'react-icons/fa';
import { 
  SiTypescript, SiPostgresql, SiMysql, 
  SiKubernetes, SiPython, SiCplusplus, SiJavascript,
  SiMongodb, SiDjango, SiExpress, SiGraphql, SiRedis,
  SiFlask, SiSnowflake, SiJenkins, SiTerraform,
  SiJira, SiSelenium, SiC, SiMicrosoftazure
} from 'react-icons/si';

// Tech icons mapping
const iconMap: { [key: string]: JSX.Element } = {
  // Programming Languages
  "Python": <SiPython color="#3776AB" />,
  "Java": <FaJava color="#ED8B00" />,
  "C": <SiC color="#A8B9CC" />,
  "C++": <SiCplusplus color="#00599C" />,
  "TypeScript": <SiTypescript color="#3178C6" />,
  "JavaScript": <SiJavascript color="#F7DF1E" />,
  "SQL": <FaDatabase color="#4479A1" />,
  "Bash": <FaTerminal color="#4EAA25" />,
  
  // Web Technologies & Frameworks
  "Node.js": <FaNodeJs color="#339933" />,
  "Django": <SiDjango color="#092E20" />,
  "Express.js": <SiExpress color="#000000" />,
  "Flask": <SiFlask color="#000000" />,
  "GraphQL": <SiGraphql color="#E10098" />,
  "React": <FaReact color="#61DAFB" />,
  
  // Databases
  "PostgreSQL": <SiPostgresql color="#336791" />,
  "MongoDB": <SiMongodb color="#47A248" />,
  "MySQL": <SiMysql color="#4479A1" />,
  "Snowflake": <SiSnowflake color="#29B5E8" />,
  "Redis": <SiRedis color="#DC382D" />,
  
  // Infrastructure & Deployment
  "Docker": <FaDocker color="#2496ED" />,
  "Kubernetes": <SiKubernetes color="#326CE5" />,
  "AWS": <FaAws color="#FF9900" />,
  "Jenkins": <SiJenkins color="#D33833" />,
  "Terraform": <SiTerraform color="#623CE4" />,
  "Azure": <SiMicrosoftazure color="#0078D4" />,
  
  // Development Tools
  "Git/GitHub": <FaGitAlt color="#F05032" />,
  "Linux": <FaLinux color="#FCC624" />,
  "JIRA": <SiJira color="#0052CC" />,
  "Selenium": <SiSelenium color="#43B02A" />,
};

// Reorganized skills by expertise level
const CORE_EXPERTISE = ["Python", "React", "Node.js", "Django", "Express.js", "JavaScript", "TypeScript"];
const FULL_STACK_ESSENTIALS = ["PostgreSQL", "MongoDB", "MySQL", "Redis", "GraphQL", "Flask"];
const SOFTWARE_ENGINEERING = ["Git/GitHub", "Docker", "AWS", "Linux", "SQL", "JIRA"];
const ADDITIONAL_SKILLS = ["C", "C++", "Java", "Kubernetes", "Jenkins", "Terraform", "Azure", "Selenium", "Snowflake", "Bash"];

// Skills organized by category with priority order
const SKILLS_BY_CATEGORY = {
  "Programming Languages": {
    priority: ["Python", "JavaScript", "TypeScript", "Java", "SQL"],
    all: ["Python", "JavaScript", "TypeScript", "Java", "C", "C++", "SQL", "Bash"]
  },
  "Web Technologies": {
    priority: ["React", "Node.js", "Django", "Express.js"],
    all: ["React", "Node.js", "Django", "Express.js", "Flask", "GraphQL"]
  },
  "Data Storage": {
    priority: ["PostgreSQL", "MongoDB", "MySQL", "Redis"],
    all: ["PostgreSQL", "MongoDB", "MySQL", "Redis", "Snowflake"]
  },
  "Infrastructure & Deployment": {
    priority: ["Docker", "AWS", "Git/GitHub", "Linux"],
    all: ["Docker", "Kubernetes", "AWS", "Azure", "Jenkins", "Terraform"]
  },
  "Development Tools": {
    priority: ["Git/GitHub", "Linux", "Docker", "JIRA"],
    all: ["Git/GitHub", "Linux", "JIRA", "Selenium"]
  }
};

const Skills: React.FC = () => {
  const [, setSkillsData] = useState<Skill[]>([]);
  const [viewMode, setViewMode] = useState<'specialized' | 'category'>('specialized');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    async function fetchSkills() {
      try {
        const data = await getSkills();
        setSkillsData(data);
      } catch (error) {
        console.warn("Error fetching skills data, using organized structure");
      }
    }

    fetchSkills();
  }, []);

  const getIcon = (skillName: string) => {
    return iconMap[skillName] || <FaReact color="#61DAFB" />;
  };

  const getSkillDescription = (skillName: string): string => {
    const descriptions: { [key: string]: string } = {
      // Core Expertise
      "Python": "Primary language - Expert in backend development, automation, data processing, and ML/AI applications",
      "React": "Go-to framework for building modern, responsive web interfaces with exceptional user experiences",
      "Node.js": "Backend expertise for scalable server applications and RESTful API development",
      "Django": "Production-ready web applications with robust security and rapid development cycles",
      "Express.js": "Lightweight APIs and microservices with efficient middleware architecture",
      "JavaScript": "Full-stack proficiency from dynamic frontends to server-side applications",
      "TypeScript": "Type-safe development ensuring maintainable and scalable codebases",
      
      // Full Stack Essentials
      "PostgreSQL": "Advanced SQL queries, optimizations, and relational database design",
      "MongoDB": "NoSQL expertise for flexible, document-based data architectures",
      "MySQL": "Reliable data persistence and complex query optimization",
      "Redis": "High-performance caching and real-time data processing",
      "GraphQL": "Efficient data fetching and modern API design patterns",
      "Flask": "Lightweight Python services and rapid prototyping",
      
      // Software Engineering
      "Git/GitHub": "Version control, collaboration, and code review best practices",
      "Docker": "Containerized deployments ensuring consistency across environments",
      "AWS": "Cloud infrastructure including EC2, S3, Lambda, and RDS",
      "Linux": "System administration and shell scripting proficiency",
      "SQL": "Database design, optimization, and complex query construction",
      "JIRA": "Agile project management and sprint planning",
      
      // Additional Skills
      "C": "Systems programming and performance-critical applications",
      "C++": "Algorithm implementation and competitive programming",
      "Java": "Object-oriented design and enterprise applications",
      "Kubernetes": "Container orchestration for scalable deployments",
      "Jenkins": "CI/CD pipeline automation",
      "Terraform": "Infrastructure as code",
      "Azure": "Microsoft cloud services",
      "Selenium": "Automated testing frameworks",
      "Snowflake": "Cloud data warehousing",
      "Bash": "Shell scripting and automation"
    };
    
    return descriptions[skillName] || "Proficient in modern development practices";
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'specialized' ? 'category' : 'specialized');
  };

  const toggleCategoryExpansion = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handlePlaylist = () => {
    const video = videoRef.current;
    
    if (video) {
      if (videoPlaying) {
        video.pause();
        setVideoPlaying(false);
      } else {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setVideoPlaying(true);
            })
            .catch(error => {
              console.error("Playback prevented by browser:", error);
            });
        }
      }
    }
  };

  return (
    <div className="netflix-skills-container">
      {/* Banner section */}
      <div className="skills-banner">
        {/* Video Player */}
        <video
          ref={videoRef}
          className={`rickroll-video ${videoPlaying ? 'playing' : ''}`}
          src="/rickroll.mp4"
          onEnded={() => setVideoPlaying(false)}
        />
        <div className="banner-content">
          <h1 className="netflix-title">TECHNICAL EXPERTISE</h1>
          
          <div className="netflix-meta">
            <span className="match">98% Match</span>
            <span className="year">2025</span>
            <span className="rating">FULL STACK</span>
            <span className="categories">Python Specialist</span>
          </div>

          <p className="netflix-description">
            Specialized in Python development and full-stack engineering, building scalable 
            applications from backend services to responsive frontends. Experienced in modern 
            web technologies with a focus on clean, maintainable code.
          </p>

          <div className="netflix-buttons">
            <button className="btn-play" onClick={toggleViewMode}>
              {viewMode === 'specialized' ? <FaTh /> : <FaLayerGroup />}
              <span>{viewMode === 'specialized' ? 'View by Category' : 'View Specialization'}</span>
            </button>
            <button 
              className={`btn-list ${videoPlaying ? 'active' : ''}`} 
              onClick={handlePlaylist}
            >
              {videoPlaying ? <FaPlay /> : <FaPlus />}
              <span>{videoPlaying ? 'Stop Video' : 'My Playlist'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Specialized View */}
      {viewMode === 'specialized' && (
        <>
          {/* Core Specialization Section */}
          <div className="specialization-section">
            <h2 className="specialization-title">
              <FaFire className="title-icon" />
              Core Specialization
            </h2>
            <div className="specialization-grid">
              <div className="specialty-card python-focus">
                <div className="specialty-header">
                  <SiPython className="specialty-icon" />
                  <div className="specialty-info">
                    <h3>Python Development</h3>
                    <span className="specialty-badge">PRIMARY EXPERTISE</span>
                  </div>
                </div>
                <p className="specialty-description">
                  Expert in Python ecosystem - from Django web applications to data processing pipelines, 
                  automation scripts, and AI/ML implementations. Proficient in writing clean, efficient, 
                  and Pythonic code.
                </p>
                <div className="related-skills">
                  {["Django", "Flask", "FastAPI", "Pandas", "NumPy"].map(skill => (
                    <span key={skill} className="related-skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
              
              <div className="specialty-card fullstack-focus">
                <div className="specialty-header">
                  <FaReact className="specialty-icon" />
                  <div className="specialty-info">
                    <h3>Full Stack Engineering</h3>
                    <span className="specialty-badge">CORE COMPETENCY</span>
                  </div>
                </div>
                <p className="specialty-description">
                  End-to-end application development from responsive React frontends to scalable 
                  Node.js/Express backends. Experienced in building RESTful APIs, real-time features, 
                  and modern web architectures.
                </p>
                <div className="related-skills">
                  {["React", "Node.js", "TypeScript", "MongoDB", "PostgreSQL"].map(skill => (
                    <span key={skill} className="related-skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Production Ready Skills */}
          <div className="skills-category-section">
            <h2 className="category-title trending">
              <FaStar className="category-icon" />
              Production Ready
            </h2>
            <div className="skills-row enhanced">
              {CORE_EXPERTISE.map((skillName, idx) => (
                <div key={idx} className="skill-item production-ready">
                  <div className="skill-badge">EXPERT</div>
                  <div className="skill-icon-section">
                    {getIcon(skillName)}
                  </div>
                  <div className="skill-info">
                    <h3 className="skill-name">{skillName}</h3>
                    <p className="skill-description">{getSkillDescription(skillName)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Full Stack Essentials */}
          <div className="skills-category-section">
            <h2 className="category-title">
              <FaRocket className="category-icon" />
              Full Stack Essentials
            </h2>
            <div className="skills-row">
              {FULL_STACK_ESSENTIALS.map((skillName, idx) => (
                <div key={idx} className="skill-item essential">
                  <div className="skill-icon-section">
                    {getIcon(skillName)}
                  </div>
                  <div className="skill-info">
                    <h3 className="skill-name">{skillName}</h3>
                    <p className="skill-description">{getSkillDescription(skillName)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Software Engineering Practices */}
          <div className="skills-category-section">
            <h2 className="category-title">
              Software Engineering Practices
            </h2>
            <div className="skills-row">
              {SOFTWARE_ENGINEERING.map((skillName, idx) => (
                <div key={idx} className="skill-item practices">
                  <div className="skill-icon-section">
                    {getIcon(skillName)}
                  </div>
                  <div className="skill-info">
                    <h3 className="skill-name">{skillName}</h3>
                    <p className="skill-description">{getSkillDescription(skillName)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Also In My Toolkit */}
          <div className="skills-category-section additional">
            <h2 className="category-title muted">
              Also In My Toolkit
            </h2>
            <div className="skills-compact-grid">
              {ADDITIONAL_SKILLS.map((skillName, idx) => (
                <div key={idx} className="skill-item-compact">
                  <div className="compact-icon">
                    {getIcon(skillName)}
                  </div>
                  <span className="compact-name">{skillName}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Category View */}
      {viewMode === 'category' && (
        <div className="category-view-container">
          {Object.entries(SKILLS_BY_CATEGORY).map(([category, categoryData]) => {
            const isExpanded = expandedCategories.has(category);
            const skillsToShow = isExpanded ? categoryData.all : categoryData.priority;
            const hasMore = categoryData.all.length > categoryData.priority.length;
            
            return (
              <div key={category} className="skills-category-section">
                <div className="category-header-wrapper">
                  <h2 className="category-title">
                    {category}
                  </h2>
                  {hasMore && !isExpanded && (
                    <button 
                      className="show-more-inline"
                      onClick={() => toggleCategoryExpansion(category)}
                    >
                      +{categoryData.all.length - categoryData.priority.length} more
                    </button>
                  )}
                </div>
                <div className="skills-row">
                  {skillsToShow.map((skillName, idx) => {
                    const isCore = CORE_EXPERTISE.includes(skillName);
                    return (
                      <div key={idx} className={`skill-item ${isCore ? 'core-highlight' : ''}`}>
                        {isCore && <div className="skill-badge">CORE</div>}
                        <div className="skill-icon-section">
                          {getIcon(skillName)}
                        </div>
                        <div className="skill-info">
                          <h3 className="skill-name">{skillName}</h3>
                          <p className="skill-description">{getSkillDescription(skillName)}</p>
                        </div>
                      </div>
                    );
                  })}
                  {hasMore && (
                    <div 
                      className="show-more-card"
                      onClick={() => toggleCategoryExpansion(category)}
                    >
                      <div className="show-more-content">
                        {isExpanded ? (
                          <>
                            <span className="show-more-text">Show Less</span>
                            <FaChevronRight className="show-more-icon rotated" />
                          </>
                        ) : (
                          <>
                            <span className="show-more-text">+{categoryData.all.length - categoryData.priority.length} more</span>
                            <FaChevronRight className="show-more-icon" />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Skills;